import React from 'react';
import { FaUserPlus, FaLink, FaSync } from 'react-icons/fa';

// Components
import StatsCards from './components/StatsCards';
import SearchFilterBar from './components/SearchFilterBar';
import UserTable from './components/UserTable';
import AddUserModal from './components/Modals/AddUserModal';
import EditUserModal from './components/Modals/EditUserModal';
import DeleteConfirmationModal from './components/Modals/DeleteConfirmationModal';
import AdminMappingModal from './components/Modals/AdminMappingModal';
import CONFIG from '../../../../src/config';
// Hooks
import useUsers from './hooks/useUsers';

// Utils
import { getRoleDisplayName, getRoleColor, getUsernameFromEmail } from './utils/userHelpers';

const SuperAdminUsersContent = () => {
  const {
  users,
  companies,
  loading,
  loadingCompanies,
  allAdmins,
  allRegularUsers,
  adminSuggestions,
  userSuggestions,
  filteredCompanies,
  searchTerm,
  setSearchTerm,
  roleFilter,
  setRoleFilter,
  companySearch,
  setCompanySearch,
  adminSearch,
  setAdminSearch,
  userSearch,
  setUserSearch,
  showAddModal,
  setShowAddModal,
  showEditModal,
  setShowEditModal,
  showDeleteConfirm,
  setShowDeleteConfirm,
  showAdminMappingModal,
  setShowAdminMappingModal,
  showPassword,
  setShowPassword,
  newUser,
  setNewUser,
  editingUser,
  setEditingUser,
  editUserData,
  setEditUserData,
  adminMappingData,
  setAdminMappingData,
  selectedUsersForMapping,
  setSelectedUsersForMapping,
  isSubmitting,
  loadingAdminMapping,
  fetchData,
  handleRefresh,
  handleAddSubmit,
  handleEditSubmit,
  handleDeleteUser,
  handleAdminUserMapping,
  filterAdminSuggestions,
  filterUserSuggestions,
  handleCompanyCheckboxChange,
  handleUserCheckboxForAdminMapping,
  openEditModal,
  openAdminMappingModal,
  getCompanyNames,
  getManagedUserNames,
  getManagedUserCount,
  handleOpenAddModal,
  getToken,
  API_BASE_URL
} = useUsers();

  // Filter users based on search term and role filter
  const filteredUsers = users.filter(user => {
    const matchesSearch = 
      (user.email && user.email.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesRole = roleFilter === 'all' || user.role === roleFilter;
    
    return matchesSearch && matchesRole;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">User Management</h2>
          <p className="text-gray-600 dark:text-gray-400">Manage users, admins, and their company access</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handleRefresh}
            className="flex items-center gap-2 px-4 py-2 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-white rounded-lg transition-colors"
            title="Refresh all data"
            disabled={loading}
          >
            <FaSync className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
          <button
            onClick={openAdminMappingModal}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
            title="Map users to admin"
          >
            <FaLink />
            Map Users to Admin
          </button>
          <button
            onClick={handleOpenAddModal}  // Changed from setShowAddModal
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
          >
            <FaUserPlus />
            Add User
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <StatsCards users={users} companies={companies} />

      {/* Search and Filter Bar */}
      <SearchFilterBar 
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        roleFilter={roleFilter}
        setRoleFilter={setRoleFilter}
      />

      {/* Users Table */}
      <UserTable 
        loading={loading}
        users={users}
        filteredUsers={filteredUsers}
        searchTerm={searchTerm}
        roleFilter={roleFilter}
        companies={companies}
        openEditModal={openEditModal}
        setShowDeleteConfirm={setShowDeleteConfirm}
        getRoleDisplayName={getRoleDisplayName}
        getRoleColor={getRoleColor}
        getUsernameFromEmail={getUsernameFromEmail}
        getCompanyNames={getCompanyNames}
        getManagedUserNames={getManagedUserNames}
        getManagedUserCount={getManagedUserCount}
      />

      {/* Show result count */}
      {filteredUsers.length > 0 && (
        <div className="flex justify-between items-center bg-white dark:bg-gray-800 rounded-xl p-4 shadow-lg">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            Showing {filteredUsers.length} of {users.length} user(s)
            {(searchTerm || roleFilter !== 'all') && ' (filtered)'}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs px-2 py-1 rounded bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">Super Admin</span>
            <span className="text-xs px-2 py-1 rounded bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">Admin</span>
            <span className="text-xs px-2 py-1 rounded bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">User</span>
          </div>
        </div>
      )}

      {/* Modals */}
      {showAdminMappingModal && (
        <AdminMappingModal
          showAdminMappingModal={showAdminMappingModal}
          setShowAdminMappingModal={setShowAdminMappingModal}
          adminMappingData={adminMappingData}
          setAdminMappingData={setAdminMappingData}
          adminSearch={adminSearch}
          setAdminSearch={setAdminSearch}
          userSearch={userSearch}
          setUserSearch={setUserSearch}
          adminSuggestions={adminSuggestions}
          userSuggestions={userSuggestions}
          selectedUsersForMapping={selectedUsersForMapping}
          setSelectedUsersForMapping={setSelectedUsersForMapping}
          loadingAdminMapping={loadingAdminMapping}
          handleAdminUserMapping={handleAdminUserMapping}
          handleUserCheckboxForAdminMapping={handleUserCheckboxForAdminMapping}
        />
      )}

      {showAddModal && (
        <AddUserModal
          showAddModal={showAddModal}
          setShowAddModal={setShowAddModal}
          newUser={newUser}
          setNewUser={setNewUser}
          showPassword={showPassword}
          setShowPassword={setShowPassword}
          isSubmitting={isSubmitting}
          companySearch={companySearch}
          setCompanySearch={setCompanySearch}
          filteredCompanies={filteredCompanies}
          loadingCompanies={loadingCompanies}
          handleAddSubmit={handleAddSubmit}
          handleCompanyCheckboxChange={handleCompanyCheckboxChange}
          companies={companies}
        />
      )}

      {showEditModal && editingUser && (
  <EditUserModal
    showEditModal={showEditModal}
    setShowEditModal={setShowEditModal}
    editingUser={editingUser}
    setEditingUser={setEditingUser}
    editUserData={editUserData}
    setEditUserData={setEditUserData}
    isSubmitting={isSubmitting}
    companySearch={companySearch}
    setCompanySearch={setCompanySearch}
    filteredCompanies={filteredCompanies}
    loadingCompanies={loadingCompanies}
    handleEditSubmit={handleEditSubmit}
    handleCompanyCheckboxChange={handleCompanyCheckboxChange}
    companies={companies} // Make sure this is passed
  />
)}
      {showDeleteConfirm && (
        <DeleteConfirmationModal
          showDeleteConfirm={showDeleteConfirm}
          setShowDeleteConfirm={setShowDeleteConfirm}
          handleDeleteUser={handleDeleteUser}
        />
      )}
    </div>
  );
};

export default SuperAdminUsersContent;